
import { namespace, Getter, State } from "vuex-class"
import { Team } from "@/types"
import { Component, Vue, Watch, Prop } from "vue-property-decorator"
import DataTable from "../DataTable.vue"
import api from "@/services/api"
import { DynamicParameter, DataType, Insight, Operator } from "@/types/insights"
import { dataTypeParameters } from "@/types/dataType"
import { DataSource } from "@/types/dataSourceItem"
import SkeletonLoader from "@/components/SkeletonLoader.vue"

import ParameterValue from "@/components/Controls/ParameterValue.vue"
import { cloneDeep } from "lodash"

const setupStore = namespace("setup")
@Component({
	components: {
		"data-table": DataTable,
		"skeleton": SkeletonLoader
	}
})
export default class GlobalParameterSetup extends Vue {
	@Prop({ default: [] })
	parameters!: DynamicParameter[]

	@setupStore.State dataSources!: DataSource[]

	currentParameter: DynamicParameter | null = null
	private loading: boolean = false;

	DataType = DataType

	// Retrieve all insight Parameters
	private async loadInsightParameters () {
		this.$emit("loadInsightParameters")
	}

	get parameterValueOptions () {
		return this.parameters.filter(p => !!p.key).toDictionaryMapValue(p => p.key, p => {
			return {
				operator: p.dataType === DataType.userRole
					? Operator.Contains
					: p.dataType === DataType.dateRange
						? Operator.DateRange
						: Operator.Equals,
				searchClauseParameters: null,
				fieldData: {
					fieldDataType: p.dataType === DataType.dateRange ? DataType.dateTime : p.dataType,
					dataSourceId: p.dataType === DataType.array ? p.dataSourceId : null,
					dataType: p.dataType === DataType.userRole ? "user-role" : null
				}
			}
		})
	}

	private get parameterTypes () {
		var parameterTypes: any[] = []

		dataTypeParameters.forEach(t => {
			var dataType = {
				id: t.dataType,
				title: t.label
			}
			parameterTypes.push(dataType)
		})
		return parameterTypes
	}

	private get parameterColumns () {
		return [
			{
				key: "name",
				label: "Name",
				sortable: true,
				errors: (item: any) => (item.name
					? (this.parameters.some(parameter => parameter.name === item.name) && !item.key ? "Parameter name already in use" : null)
					: "Please enter a name")
			},
			{
				key: "dataType",
				label: "Data Type",
				hasTemplate: true,
				dataType: "list",
				valueColumn: "dataType",
				options: this.parameterTypes,
				errors: (item: any) => (item.dataType === null ? "Please Select a Data Type" : "")
			},
			{
				key: "dataSourceId",
				label: "Data Source",
				dataType: "list",
				valueColumn: "dataSourceId",
				options: this.dataSources,
				inModal: (item: any) => item.dataType === 4,
				inTable: false,
				errors: (item: any) => (item.dataType === 4 && !item.dataSourceId ? "Please Enter a Data Source" : "")
			},
			{
				key: "defaultValue",
				label: "Default Value",
				inTable: false,
				dynamicComponent: ParameterValue,
				componentProperties: {
					operator: !this.currentParameter
						? Operator.Equals
						: this.currentParameter.dataType === DataType.userRole
							? Operator.Contains
							: this.currentParameter.dataType === DataType.dateRange
								? Operator.DateRange
								: Operator.Equals,
					searchClauseParameters: this.currentParameter
						? this.currentParameter.searchClauseParameters
						: null,
					clauseIndex: this.currentParameter
						? this.currentParameter.key
						: null,
					dynamicValue: this.currentParameter
						? this.currentParameter.dynamicValue
						: false,
					field: this.currentParameter
						? {
							fieldDataType: this.currentParameter.dataType === DataType.dateRange
								? DataType.dateTime
								: this.currentParameter.dataType,
							dataSourceId: this.currentParameter.dataType === DataType.array ? this.currentParameter.dataSourceId : null,
							dataType: this.currentParameter.dataType === DataType.userRole ? "user-role" : null
						}
						: null
				},
				eventHandlers: {
					searchClauseParametersChanged:
						(searchClauseParameters: any) => {
							if (this.currentParameter) {
								Vue.set(this.currentParameter, "searchClauseParameters", searchClauseParameters)
							}
						},
					dynamicValueChanged:
						(dynamicValue: boolean) => {
							if (this.currentParameter) {
								Vue.set(this.currentParameter, "dynamicValue", dynamicValue)
							}
						}
				}
			}
		]
	}

	private async saveParameter (parameter: DynamicParameter) {
		this.loading = true
		const updatedParameter = await api.insightParameterSave(parameter)

		await this.loadInsightParameters()
		this.loading = false
	}

	private createParameter () {
		var parameter: DynamicParameter = {
			key: "",
			name: "",
			dataType: 0,
			dataSourceId: null
		}
		this.currentParameter = parameter
		return parameter
	}

	private async deleteParameter (parameter: DynamicParameter) {
		if (!parameter.key) return

		this.loading = true
		await api.insightParameterDelete(parameter)
		await this.loadInsightParameters()

		// Remove from any insights
		this.$emit("parameterDeleted", parameter)
		this.loading = false
	}

	private parameterTypeName (typeId: number) {
		return this.parameterTypes.find(t => t.id === typeId).title
	}

	private selectParameter (parameter: DynamicParameter) {
		this.currentParameter = cloneDeep(parameter)
		return this.currentParameter
	}
}

